import React, { useEffect, useState } from "react";
import {
  FaBalanceScale,
  FaCookieBite,
  FaDatabase,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFileAlt,
  FaGavel,
  FaGlobe,
  FaHandshake,
  FaIdCard,
  FaLock,
  FaMedal,
  FaShieldAlt,
  FaTrashAlt,
  FaTrophy,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import { getSettings } from "../../services/settingsServices";

const APP_LOGO = "/logo_monapplitournoi_512.png";

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  tel_club: "",
  siret_club: "",
};

const fallback = (value, defaultValue = "Non renseigné") => {
  const cleaned = String(value || "").trim();
  return cleaned || defaultValue;
};

const DataBadge = ({ children }) => {
  return (
    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
      {children}
    </span>
  );
};

const InfoCard = ({ icon: Icon, title, children }) => {
  return (
    <article className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300">
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-950 dark:text-white">
            {title}
          </h2>

          <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {children}
          </div>
        </div>
      </div>
    </article>
  );
};

const SmallDataBox = ({ title, children }) => {
  return (
    <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/50">
      <h3 className="font-bold text-slate-950 dark:text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {children}
      </p>
    </div>
  );
};

const PolitiqueConfidentialitePage = () => {
  useScrollToTop();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();

        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setSettings({
            ...DEFAULT_SETTINGS,
            ...data[0],
          });
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const currentYear = new Date().getFullYear();
  const siteUrl = fallback(settings.siteweb_club, "MonAppliTournoi");
  const contactEmail = fallback(settings.mail_club, "monappliclub@gmail.com");

  return (
    <main className="min-h-screen bg-slate-50 pt-[120px] text-slate-950 dark:bg-slate-950 dark:text-white">
      <section className="relative overflow-hidden px-4 pb-16 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute -left-24 top-20 h-80 w-80 rounded-full bg-blue-400/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-80 h-80 w-80 rounded-full bg-orange-400/15 blur-3xl" />

        <div className="relative mx-auto max-w-[1180px]">
          <header className="overflow-hidden rounded-[2.25rem] bg-slate-950 text-white shadow-2xl shadow-slate-950/20">
            <div className="relative p-6 sm:p-8 lg:p-10">
              <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-orange-500/25 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -right-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-3xl">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-200">
                    <FaUserShield className="h-3.5 w-3.5" />
                    Protection des données
                  </div>

                  <h1 className="mt-5 text-3xl font-bold tracking-tight sm:text-5xl">
                    Politique de confidentialité
                  </h1>

                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">
                    Cette politique explique comment MonAppliTournoi collecte,
                    utilise, protège et conserve les données personnelles dans
                    le cadre d’une application web de gestion de tournois
                    interclubs de Judo / Jujitsu.
                  </p>
                </div>

                <div className="flex justify-center lg:justify-end">
                  <div className="rounded-[2rem] border border-white/10 bg-white/10 p-4 backdrop-blur">
                    <img
                      src={APP_LOGO}
                      alt="Logo MonAppliTournoi"
                      className="h-28 w-28 rounded-[1.75rem] object-contain sm:h-32 sm:w-32"
                    />
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="mt-8 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <InfoCard icon={FaIdCard} title="1. Responsable du traitement">
              <p>
                Les traitements de données réalisés dans le cadre du site et de
                l’application <strong>MonAppliTournoi</strong> sont placés sous
                la responsabilité de l’éditeur du service ou, selon les cas, de
                l’organisateur de la compétition qui utilise l’application.
              </p>

              <ul className="space-y-2">
                <li>
                  <strong>Nom du service :</strong> MonAppliTournoi
                </li>
                <li>
                  <strong>Site web :</strong> {siteUrl}
                </li>
                <li>
                  <strong>Responsable de publication :</strong>{" "}
                  {fallback(settings.responsable_club, "Laurent VICTORIN")}
                </li>
                <li>
                  <strong>Contact :</strong>{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="font-bold text-orange-600 underline underline-offset-4 dark:text-orange-300"
                  >
                    {contactEmail}
                  </a>
                </li>
                <li>
                  <strong>Téléphone :</strong>{" "}
                  {fallback(settings.tel_club, "06 80 72 77 07")}
                </li>
                <li>
                  <strong>SIRET :</strong>{" "}
                  {fallback(settings.siret_club, "À compléter si applicable")}
                </li>
              </ul>
            </InfoCard>

            <InfoCard icon={FaGlobe} title="2. Application concernée">
              <p>
                Cette politique s’applique au site vitrine et aux espaces
                applicatifs de MonAppliTournoi : création de compte, espace
                compétition, import des compétiteurs, formulaires clubs invités,
                gestion des pesées, poules, tableaux, tapis, scoreboards,
                résultats publics, statistiques, contact et support.
              </p>

              <div className="flex flex-wrap gap-2">
                <DataBadge>Site vitrine</DataBadge>
                <DataBadge>Espace utilisateur</DataBadge>
                <DataBadge>Gestion tournoi</DataBadge>
                <DataBadge>Résultats publics</DataBadge>
                <DataBadge>Formulaire contact</DataBadge>
                <DataBadge>Paiement externe</DataBadge>
              </div>
            </InfoCard>
          </div>

          <div className="mt-5 grid gap-5">
            <InfoCard icon={FaDatabase} title="3. Données personnelles collectées">
              <p>
                Les données collectées varient selon l’utilisation du service.
                MonAppliTournoi ne demande que les informations utiles à la
                gestion du compte, à l’organisation sportive ou au support.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <SmallDataBox title="Compte utilisateur">
                  Nom, prénom, email, rôle, identifiants de connexion, date de
                  création du compte, état de la formule ou des droits d’accès.
                </SmallDataBox>

                <SmallDataBox title="Compétiteurs">
                  Nom, prénom, club, catégorie, sexe, année ou date de
                  naissance si nécessaire, poids, grade, numéro de licence si
                  utilisé, présence, statut de pesée.
                </SmallDataBox>

                <SmallDataBox title="Tournoi">
                  Catégories, tapis, distributions, poules, tableaux, combats,
                  vainqueurs, classements, podiums et statistiques sportives.
                </SmallDataBox>

                <SmallDataBox title="Clubs invités">
                  Nom du club, responsables, coordonnées transmises, listes de
                  combattants, informations envoyées via formulaire.
                </SmallDataBox>

                <SmallDataBox title="Contact et assistance">
                  Nom, email, téléphone si indiqué dans le message, contenu de
                  la demande, échanges liés à la démonstration ou au support.
                </SmallDataBox>

                <SmallDataBox title="Données techniques">
                  Adresse IP, navigateur, appareil, pages consultées, logs
                  techniques, dates de connexion, erreurs ou événements de
                  sécurité.
                </SmallDataBox>
              </div>

              <p>
                Les données concernant des mineurs peuvent être traitées lorsque
                cela est nécessaire à l’organisation sportive. L’organisateur
                doit veiller à limiter les données importées ou publiées au strict
                nécessaire.
              </p>
            </InfoCard>

            <InfoCard icon={FaTrophy} title="4. Finalités des traitements">
              <p>
                Les données sont utilisées pour permettre le fonctionnement de
                MonAppliTournoi et l’organisation des compétitions.
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>créer et gérer les comptes utilisateurs ;</li>
                <li>attribuer les rôles et droits d’accès ;</li>
                <li>préparer et paramétrer les compétitions ;</li>
                <li>importer ou saisir les compétiteurs ;</li>
                <li>gérer les pesées, présences et catégories ;</li>
                <li>générer les poules, tableaux et repêchages ;</li>
                <li>affecter les combats aux tapis et tables d’arbitrage ;</li>
                <li>afficher les scoreboards et résultats publics ;</li>
                <li>produire des classements, podiums et statistiques ;</li>
                <li>répondre aux demandes envoyées via le formulaire contact ;</li>
                <li>gérer les accès aux formules payantes ;</li>
                <li>sécuriser l’application et prévenir les abus.</li>
              </ul>
            </InfoCard>

            <InfoCard icon={FaBalanceScale} title="5. Bases légales">
              <p>
                Selon les fonctionnalités utilisées, les traitements peuvent
                reposer sur plusieurs bases légales :
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong>exécution d’un service demandé :</strong> création de
                  compte, accès à une compétition, utilisation des fonctionnalités
                  de l’application ;
                </li>
                <li>
                  <strong>intérêt légitime :</strong> sécurité, maintenance,
                  amélioration du service, prévention des abus, statistiques
                  techniques ;
                </li>
                <li>
                  <strong>consentement :</strong> certains cookies ou traceurs
                  non strictement nécessaires, lorsqu’ils sont utilisés ;
                </li>
                <li>
                  <strong>obligation légale :</strong> conservation de certaines
                  informations liées à la facturation, à la comptabilité ou à la
                  preuve lorsque cela est applicable.
                </li>
              </ul>
            </InfoCard>

            <InfoCard icon={FaUsers} title="6. Destinataires des données">
              <p>
                Les données peuvent être consultées uniquement par les personnes
                ou services ayant besoin d’y accéder pour la finalité prévue.
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>l’éditeur de MonAppliTournoi pour l’administration et le support ;</li>
                <li>l’organisateur de la compétition et ses utilisateurs autorisés ;</li>
                <li>les tables d’arbitrage, selon les droits accordés ;</li>
                <li>les coachs ou visiteurs, uniquement pour les résultats publics activés ;</li>
                <li>les prestataires techniques nécessaires à l’hébergement et au fonctionnement ;</li>
                <li>le prestataire de paiement lorsque l’utilisateur souscrit une formule payante.</li>
              </ul>

              <p>
                Les données ne sont pas vendues à des tiers.
              </p>
            </InfoCard>

            <InfoCard icon={FaMedal} title="7. Résultats publics">
              <p>
                MonAppliTournoi peut permettre l’affichage public des résultats
                sportifs en temps réel : noms des compétiteurs, clubs,
                catégories, combats, classements, podiums et statistiques.
              </p>

              <p>
                Cette publication dépend des options activées par l’organisateur.
                L’organisateur doit vérifier que les informations affichées
                publiquement sont nécessaires, exactes et adaptées au contexte de
                la compétition.
              </p>

              <p>
                En cas de demande légitime concernant un affichage public,
                l’éditeur ou l’organisateur peut corriger, masquer, limiter ou
                supprimer certaines informations.
              </p>
            </InfoCard>

            <InfoCard icon={FaHandshake} title="8. Prestataires et sous-traitants">
              <p>
                Pour fonctionner, MonAppliTournoi peut s’appuyer sur des
                prestataires techniques, notamment pour l’hébergement,
                l’envoi de données, la base de données, la sécurité, les paiements
                ou la maintenance.
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong>Hébergement :</strong> serveur, API, base de données et
                  stockage technique.
                </li>
                <li>
                  <strong>Paiement :</strong> les paiements éventuels sont traités
                  par un prestataire externe sécurisé. MonAppliTournoi ne stocke
                  pas les numéros complets de carte bancaire.
                </li>
                <li>
                  <strong>Maintenance :</strong> accès limité aux données
                  strictement nécessaires au support et à la résolution
                  d’incidents.
                </li>
              </ul>
            </InfoCard>

            <InfoCard icon={FaLock} title="9. Sécurité des données">
              <p>
                Des mesures raisonnables sont mises en œuvre pour protéger les
                données : authentification, rôles utilisateurs, limitation des
                accès, sauvegardes, séparation des espaces sensibles, hébergement
                sécurisé et surveillance des erreurs techniques.
              </p>

              <p>
                L’utilisateur doit également contribuer à la sécurité en
                conservant ses identifiants confidentiels, en utilisant un mot de
                passe suffisamment robuste et en signalant toute utilisation
                suspecte de son compte.
              </p>

              <p>
                Malgré ces mesures, aucun service en ligne ne peut garantir une
                sécurité absolue. En cas d’incident significatif, les mesures
                nécessaires seront prises selon la réglementation applicable.
              </p>
            </InfoCard>

            <InfoCard icon={FaTrashAlt} title="10. Durées de conservation">
              <p>
                Les données sont conservées pendant une durée adaptée à leur
                finalité. Les durées peuvent varier selon la nature des données,
                le type de compte et la compétition concernée.
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>
                  les comptes utilisateurs sont conservés tant que le compte est
                  actif ou nécessaire au service ;
                </li>
                <li>
                  les données de compétition peuvent être conservées pour
                  l’historique sportif, les résultats, les statistiques ou la
                  preuve d’organisation ;
                </li>
                <li>
                  les messages de contact sont conservés le temps nécessaire au
                  traitement de la demande ;
                </li>
                <li>
                  les logs techniques sont conservés pour la sécurité, la
                  maintenance et la prévention des abus ;
                </li>
                <li>
                  les données liées à une transaction peuvent être conservées
                  selon les obligations comptables et fiscales applicables.
                </li>
              </ul>
            </InfoCard>

            <InfoCard icon={FaGavel} title="11. Droits des personnes">
              <p>
                Les personnes concernées peuvent demander l’accès, la
                rectification, l’effacement, la limitation, l’opposition au
                traitement de leurs données et, lorsque cela est applicable, la
                portabilité de certaines données.
              </p>

              <p>
                Pour exercer ces droits, une demande peut être adressée à :
              </p>

              <p>
                <a
                  href={`mailto:${contactEmail}`}
                  className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-4 py-2 font-bold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-600"
                >
                  <FaEnvelope className="h-4 w-4" />
                  {contactEmail}
                </a>
              </p>

              <p>
                La demande doit préciser les données concernées et permettre
                d’identifier la personne. Une vérification d’identité peut être
                demandée en cas de doute raisonnable.
              </p>

              <p>
                En cas de difficulté non résolue, la personne concernée peut
                introduire une réclamation auprès de la CNIL.
              </p>
            </InfoCard>

            <InfoCard icon={FaCookieBite} title="12. Cookies et traceurs">
              <p>
                Le site peut utiliser des cookies et traceurs nécessaires à son
                fonctionnement : connexion, authentification, sécurité,
                préférences d’affichage ou maintien de session.
              </p>

              <p>
                Certains traceurs de mesure d’audience strictement nécessaires ou
                respectant les conditions d’exemption peuvent être utilisés sans
                consentement préalable. Les cookies non nécessaires, lorsqu’ils
                sont utilisés, doivent faire l’objet d’une information et d’un
                consentement lorsque la réglementation l’exige.
              </p>

              <p>
                L’utilisateur peut configurer son navigateur pour limiter ou
                refuser certains cookies. Le refus de cookies nécessaires peut
                empêcher l’accès à certaines fonctionnalités de l’application.
              </p>
            </InfoCard>

            <InfoCard icon={FaFileAlt} title="13. Formulaires et messages de contact">
              <p>
                Lorsque l’utilisateur envoie un message via le formulaire de
                contact, les données renseignées servent uniquement à répondre à
                sa demande : démonstration, devis, question technique, assistance
                ou information sur les formules.
              </p>

              <p>
                Si l’utilisateur indique volontairement un numéro de téléphone
                dans le message, celui-ci peut être utilisé pour le recontacter
                dans le cadre de sa demande.
              </p>
            </InfoCard>

            <InfoCard icon={FaShieldAlt} title="14. Mineurs et compétitions sportives">
              <p>
                Les compétitions de Judo / Jujitsu peuvent concerner des mineurs.
                Les données les concernant doivent être traitées avec une
                attention particulière.
              </p>

              <p>
                L’organisateur doit s’assurer que les données importées sont
                nécessaires à l’événement, qu’elles sont exactes, et que leur
                publication éventuelle dans les résultats publics est compatible
                avec le cadre de la compétition et les règles applicables.
              </p>
            </InfoCard>

            <InfoCard icon={FaExternalLinkAlt} title="15. Liens externes">
              <p>
                Le site peut contenir des liens vers des services externes,
                notamment le prestataire de paiement, des pages d’information ou
                des ressources partenaires.
              </p>

              <p>
                MonAppliTournoi n’est pas responsable des pratiques de
                confidentialité des sites tiers. L’utilisateur est invité à
                consulter leurs propres politiques de confidentialité.
              </p>
            </InfoCard>

            <InfoCard icon={FaBalanceScale} title="16. Modification de la politique">
              <p>
                La présente politique peut être modifiée afin de tenir compte des
                évolutions du service, de la réglementation, des fonctionnalités
                ou des prestataires utilisés.
              </p>

              <p>
                La version applicable est celle publiée sur le site au moment de
                la consultation.
              </p>
            </InfoCard>
          </div>

          <footer className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
            <p>
              Dernière mise à jour : {currentYear}. Cette politique de
              confidentialité complète les mentions légales et les conditions
              d’utilisation de MonAppliTournoi.
            </p>
          </footer>
        </div>
      </section>
    </main>
  );
};

export default PolitiqueConfidentialitePage;